import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { Label, Grid, SectionHeader, PageHeader, css, Toast, MontoInput } from '../components/UI';
import { parseCFDI, leerArchivo, datosFiscales, soloFiscales } from '../cfdi';
import { validarCLABE, hoyISO } from '../utils';

const ACCENT_DEFAULT = '#ff541c'; // naranja Selectshop (hex: se le concatena opacidad)

function ComprobadorSearch({ value, onChange, onSelect }) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const wrap = useRef(null);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try { const d = await api.get(`/comprobadores?q=${encodeURIComponent(q)}`); setResults(d.items || []); }
    catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(value), 280);
    return () => clearTimeout(timer.current);
  }, [value, search]);

  useEffect(() => {
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <input
        value={value}
        placeholder="Escribe para buscar comprobador..."
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        style={css.input(false)}
      />
      {open && (results.length > 0 || loading) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
          background: 'var(--panel)', border: '1px solid var(--hair)', borderRadius: 10,
          boxShadow: '0 12px 32px rgba(15,23,42,0.14)', maxHeight: 260, overflowY: 'auto', marginTop: 4,
        }}>
          {loading && <div style={{ padding: '10px 14px', color: 'var(--glass)', fontSize: 13 }}>Buscando...</div>}
          {results.map((c, i) => (
            <div key={c._id || i} onMouseDown={() => { onSelect(c); setOpen(false); }}
              style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: i < results.length - 1 ? '1px solid var(--hair)' : 'none' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,84,28,0.10)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{c.nombre}</div>
              {c.conceptos?.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 1 }}>
                  {c.conceptos.length} concepto{c.conceptos.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ConceptoSearch({ value, onChange, hasError, conceptos, placeholder, placeholderVacio, style }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const filtered = value.length > 0
    ? conceptos.filter(c => c.toLowerCase().includes(value.toLowerCase()))
    : conceptos;

  useEffect(() => {
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <input
        value={value}
        placeholder={conceptos.length
          ? (placeholder || 'Selecciona el concepto de gasto...')
          : (placeholderVacio || 'Selecciona primero un comprobador...')}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        disabled={conceptos.length === 0}
        style={{ ...css.input(hasError), ...(conceptos.length === 0 ? css.inputRO : {}), ...style }}
      />
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
          background: 'var(--panel)', border: '1px solid var(--hair)', borderRadius: 10,
          boxShadow: '0 12px 32px rgba(15,23,42,0.14)', maxHeight: 220, overflowY: 'auto', marginTop: 4,
        }}>
          {filtered.map((c, i) => (
            <div key={c} onMouseDown={() => { onChange(c); setOpen(false); }}
              style={{
                padding: '9px 14px', cursor: 'pointer', fontSize: 13,
                borderBottom: i < filtered.length - 1 ? '1px solid var(--hair)' : 'none',
                color: 'var(--ink-soft)',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,84,28,0.10)'; e.currentTarget.style.color = '#d63500'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--ink-soft)'; }}>
              {c}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Desglose de la comprobación por motivo de pago. Una comprobación junta varios
// gastos y cada uno tiene su propio motivo —y por tanto su propia cuenta contable—
// y su propia factura, así que el motivo, el monto y los archivos viven por renglón.
// Es el mismo formato que caja chica y reembolso de gastos en NuevaSolicitud.js.
function ConceptosComprobacion({ conceptos, onChange, motivos, hasError, onImportarXML, onAdjuntarPDF, importandoIdx }) {
  const upd = (i, campo, val) => onChange(conceptos.map((c, idx) => idx === i ? { ...c, [campo]: val } : c));
  const add = () => onChange([...conceptos, { motivo: '', monto: '', _archivos: [] }]);
  const quitar = (i) => { if (conceptos.length <= 1) return; onChange(conceptos.filter((_, idx) => idx !== i)); };
  const quitarArchivo = (i, nombre) => onChange(conceptos.map((c, idx) => idx !== i ? c
    : { ...c, _archivos: (c._archivos || []).filter(a => a.name !== nombre) }));
  const total = conceptos.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const fmt = (n) => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
  const btn = (color) => ({
    padding: '3px 9px', borderRadius: 6, fontSize: 10.5, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'Inter, sans-serif', border: `1px solid ${color}44`, background: `${color}14`, color,
  });

  return (
    <div>
      <div style={{ fontSize: 11.5, color: 'var(--glass)', marginBottom: 6 }}>
        Cada motivo se clasifica en su propia cuenta contable y lleva su factura. El monto de la
        comprobación es la suma de todos.
      </div>
      {conceptos.map((c, i) => {
        const motivoErr = hasError && !c.motivo?.trim();
        const montoErr  = hasError && (!c.monto || isNaN(parseFloat(c.monto)) || parseFloat(c.monto) <= 0);
        const archivos = c._archivos || [];
        return (
          <div key={i} style={{
            border: '1px solid var(--hair)', borderRadius: 10, padding: 10, marginBottom: 8,
            background: 'var(--panel)',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 34px', gap: 8, alignItems: 'start' }}>
              <ConceptoSearch value={c.motivo} onChange={v => upd(i, 'motivo', v)} hasError={motivoErr}
                conceptos={motivos} placeholder="Busca el motivo de pago..."
                placeholderVacio="Cargando motivos de pago..." />
              <MontoInput value={c.monto} onChange={v => upd(i, 'monto', v)} err={montoErr} />
              <button onClick={() => quitar(i)} disabled={conceptos.length <= 1} title="Quitar motivo"
                style={{ borderRadius: 7, border: '1px solid rgba(239,68,68,0.28)', background: 'rgba(239,68,68,0.10)',
                  color: 'var(--danger)', cursor: conceptos.length <= 1 ? 'not-allowed' : 'pointer', fontSize: 13,
                  opacity: conceptos.length <= 1 ? 0.3 : 1, fontFamily: 'Inter, sans-serif' }}>🗑</button>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 7, flexWrap: 'wrap' }}>
              <button onClick={() => onImportarXML(i)} disabled={importandoIdx === i} style={btn('#15803d')}
                title="Lee el XML de la factura de ESTE motivo: toma sus impuestos reales y su folio fiscal">
                {importandoIdx === i ? '⏳ Leyendo...' : '📄 XML'}
              </button>
              <button onClick={() => onAdjuntarPDF(i)} style={btn('#0369a1')} title="Adjunta el PDF de la factura de este motivo">
                📎 PDF
              </button>
              {c.folioCfdi && (
                <span title={`${c.nombreEmisor || ''}\nUUID ${c.folioCfdi}`} style={{
                  padding: '2px 8px', borderRadius: 5, fontSize: 10, fontWeight: 600,
                  background: 'rgba(22,163,74,0.10)', color: 'var(--ok)', border: '1px solid rgba(22,163,74,0.28)',
                  maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>📋 {c.nombreEmisor || c.rfcEmisor || 'CFDI'}</span>
              )}
              {archivos.map(a => (
                <span key={a.name} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 5,
                  fontSize: 10, background: 'var(--hair-soft)', border: '1px solid var(--hair)', color: 'var(--ink-soft)',
                  maxWidth: 200,
                }}>
                  <span>{a.name.toLowerCase().endsWith('.xml') ? '📋' : '📄'}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                  <button onClick={() => quitarArchivo(i, a.name)} title="Quitar archivo"
                    style={{ border: 'none', background: 'transparent', color: 'var(--danger)', cursor: 'pointer', fontSize: 11, padding: 0 }}>✕</button>
                </span>
              ))}
            </div>
          </div>
        );
      })}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={add} style={{
          padding: '5px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600,
          border: '1.5px dashed var(--hair-strong)', background: 'var(--hair-soft)', color: 'var(--glass)',
          cursor: 'pointer', fontFamily: 'Inter, sans-serif',
        }}>+ Agregar motivo de pago</button>
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
          <span style={{ fontSize: 10, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total de la comprobación</span>
          <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--plasma)', fontFamily: 'Geist Mono, monospace' }}>{fmt(total)}</span>
        </div>
      </div>
      {hasError && <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 5 }}>Cada motivo necesita nombre y un monto mayor a cero</div>}
    </div>
  );
}

export default function NuevaComprobacion({ onCreated }) {
  const { user } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [sucursalesAll, setSucursalesAll] = useState([]);
  const [centros, setCentros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [errors, setErrors] = useState({});
  const [conceptosComprobador, setConceptosComprobador] = useState([]);

  const [form, setForm] = useState({
    empresa: '', sucursal: '', centroCostos: '', direccion: '',
    comprobador: '', banco: '', clabe: '', cuenta: '',
    conceptoGasto: '', monto: '', moneda: 'MXN',
    folioCfdi: '',
    fechaSolicitud: hoyISO(),
    observaciones: '',
  });
  const xmlInputRef = useRef(null);
  const [importando, setImportando] = useState(false);

  // Desglose por motivo de pago (catálogo de Motivos de Pago, el mismo de los
  // gastos en general). Cada renglón lleva su factura: XML para los datos
  // fiscales y PDF para el expediente.
  const [motivosCatalogo, setMotivosCatalogo] = useState([]);
  const [conceptos, setConceptos] = useState([{ motivo: '', monto: '', _archivos: [] }]);
  const [importandoIdx, setImportandoIdx] = useState(null);
  const cXmlRefs = useRef({}); // ref por índice de concepto
  const cPdfRefs = useRef({});
  const totalConceptos = conceptos.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);

  useEffect(() => {
    api.get('/empresas').then(all => {
      if (user?.rol === 'solicitante' && user?.empresas?.length) {
        const propias = all.filter(e => user.empresas.includes(e.nombre));
        setEmpresas(propias);
        if (propias.length === 1) setForm(f => ({ ...f, empresa: propias[0].nombre }));
      } else {
        setEmpresas(all);
      }
    }).catch(() => {});
    api.get('/sucursales').then(setSucursalesAll).catch(() => {});
    // Catálogo de Motivos de Pago para el desglose por motivo
    api.get('/motivos').then(l => setMotivosCatalogo((l || []).map(m => m.nombre).filter(Boolean))).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!form.sucursal) { setCentros([]); return; }
    api.get(`/centros?sucursal=${encodeURIComponent(form.sucursal)}`)
      .then(list => setCentros(
        user?.rol === 'solicitante' && user?.centrosCostos?.length
          ? list.filter(c => user.centrosCostos.includes(c.nombre))
          : list
      ))
      .catch(() => setCentros([]));
  }, [form.sucursal]);

  const empresaObj = empresas.find(e => e.nombre === form.empresa);
  // Acento fijo: el color NO cambia al seleccionar empresa
  const accent = ACCENT_DEFAULT;

  const sucursalesBase = empresaObj?.sucursales?.length
    ? sucursalesAll.filter(s => empresaObj.sucursales.includes(s.nombre))
    : sucursalesAll;
  // Si el solicitante tiene sucursales asignadas, acota a ellas
  const sucursalesFiltradas = (user?.rol === 'solicitante' && user?.sucursales?.length)
    ? sucursalesBase.filter(s => user.sucursales.includes(s.nombre))
    : sucursalesBase;

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (errors[k]) setErrors(e => ({ ...e, [k]: false })); };

  const handleEmpresa = (nombre) => {
    setForm(f => ({ ...f, empresa: nombre, sucursal: '', centroCostos: '', direccion: '' }));
    if (errors.empresa) setErrors(e => ({ ...e, empresa: false }));
  };
  const handleSucursal = (s) => { set('sucursal', s); set('centroCostos', ''); };
  const handleCentro = (nombre) => {
    const centroObj = centros.find(cc => cc.nombre === nombre);
    const dir = centroObj?.seccion?.trim() || centroObj?.direccion?.trim() || '';
    setForm(f => ({ ...f, centroCostos: nombre, ...(dir ? { direccion: dir } : {}) }));
    if (errors.centroCostos) setErrors(e => ({ ...e, centroCostos: false }));
  };

  const handleComprobador = (c) => {
    setForm(f => ({
      ...f,
      comprobador: c.nombre,
      banco: c.banco || '',
      clabe: c.clabe || '',
      cuenta: c.cuenta || '',
      conceptoGasto: '',
    }));
    setConceptosComprobador(c.conceptos || []);
    if (errors.comprobador) setErrors(e => ({ ...e, comprobador: false }));
  };

  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4000); };

  // Lee uno o varios XML (CFDI) y autocompleta monto y folio(s) fiscal(es).
  const handleImportXML = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.name.toLowerCase().endsWith('.xml'));
    if (!files.length) return;
    setImportando(true);
    try {
      let suma = 0;
      const folios = [];
      const errores = [];
      let moneda = form.moneda;
      const leidos = [];
      for (const file of files) {
        try {
          const cfdi = parseCFDI(await leerArchivo(file), file.name);
          suma += (cfdi.total || 0) * (cfdi.esEgreso ? -1 : 1);   // nota de crédito (Egreso) resta
          const folio = cfdi.folioFiscal || cfdi.referencia;
          if (folio) folios.push(folio);
          if (cfdi.moneda) moneda = cfdi.moneda;
          leidos.push(cfdi);
        } catch (e) { errores.push(e.message); }
      }
      if (!folios.length && suma === 0) {
        toast_(errores[0] || 'No se pudo leer ningún XML', 'error');
        return;
      }
      // Folios ya presentes (para no duplicar al importar varias veces)
      const previos = (form.folioCfdi || '').split(',').map(s => s.trim()).filter(Boolean);
      const todos = [...new Set([...previos, ...folios])];
      setForm(f => ({
        ...f,
        monto: suma > 0 ? String(suma.toFixed(2)) : f.monto,
        folioCfdi: todos.join(', '),
        moneda,
        // Con varios CFDI no hay un emisor ni un total únicos que atribuir
        ...(leidos.length === 1 ? datosFiscales(leidos[0]) : {}),
      }));
      setErrors(e => ({ ...e, monto: false }));
      let msg = `${files.length - errores.length} XML leído${files.length - errores.length !== 1 ? 's' : ''}`;
      if (errores.length) msg += ` · ${errores.length} con error`;
      toast_(msg, errores.length ? 'error' : 'ok');
    } finally {
      setImportando(false);
      if (xmlInputRef.current) xmlInputRef.current.value = '';
    }
  };

  // XML de la factura de UN motivo: llena sus datos fiscales e impuestos reales y
  // deja el archivo listo para subirlo al expediente al guardar.
  const handleImportXMLConcepto = async (idx, fileList) => {
    const files = Array.from(fileList || []).filter(f => f.name.toLowerCase().endsWith('.xml'));
    if (!files.length) return;
    setImportandoIdx(idx);
    try {
      const cfdi = parseCFDI(await leerArchivo(files[0]), files[0].name);
      setConceptos(prev => prev.map((c, i) => i !== idx ? c : {
        ...c,
        folioCfdi: cfdi.folioFiscal || cfdi.referencia || '',
        serie: cfdi.serie || '',
        usoCfdi: cfdi.usoCfdi || '',
        metodoPago: cfdi.metodoPago || '',
        fechaTimbrado: cfdi.fechaTimbrado || '',
        ...datosFiscales(cfdi),
        // El monto capturado manda: la factura puede amparar más de lo que se
        // comprueba en este renglón.
        monto: c.monto || (cfdi.total ? String(cfdi.total.toFixed(2)) : ''),
        _impuestos: cfdi.impuestos || [],
        _impuestosImportes: cfdi.impuestosImportes || {},
        _archivos: [...(c._archivos || []), files[0]],
      }));
      if (cfdi.sinTimbrar) toast_(`⚠️ El XML de ${cfdi.referencia} NO está timbrado: no ampara el gasto.`, 'error');
      else toast_(`Motivo ${idx + 1}: XML de ${cfdi.nombreEmisor || cfdi.rfcEmisor} importado`);
    } catch (e) { toast_(e.message, 'error'); }
    finally { setImportandoIdx(null); if (cXmlRefs.current[idx]) cXmlRefs.current[idx].value = ''; }
  };

  const handleAdjuntarPDFConcepto = (idx, fileList) => {
    const files = Array.from(fileList || []).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (!files.length) return;
    setConceptos(prev => prev.map((c, i) => i !== idx ? c : { ...c, _archivos: [...(c._archivos || []), ...files] }));
    if (cPdfRefs.current[idx]) cPdfRefs.current[idx].value = '';
  };

  // Sube al expediente los archivos de cada motivo, etiquetados con su motivo para
  // que en Captura de Comprobantes se vea a cuál pertenece cada factura.
  const subirArchivosConceptos = async (compId) => {
    for (const c of conceptos) {
      const archivos = c._archivos || [];
      if (!archivos.length) continue;
      const fd = new FormData();
      archivos.forEach(f => fd.append('archivos', f));
      if (c.motivo?.trim()) fd.append('concepto', c.motivo.trim());
      await api.upload(`/comprobaciones/${compId}/comprobantes`, fd);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.empresa) e.empresa = true;
    if (!form.sucursal) e.sucursal = true;
    if (!form.centroCostos) e.centroCostos = true;
    if (!form.comprobador) e.comprobador = true;
    if (!form.conceptoGasto.trim()) e.conceptoGasto = true;
    // El monto ya no se captura: es la suma de los motivos, así que se valida ahí
    const conceptosInvalidos = !conceptos.length ||
      conceptos.some(c => !c.motivo?.trim() || !c.monto || isNaN(parseFloat(c.monto)) || parseFloat(c.monto) <= 0);
    if (conceptosInvalidos) e.conceptos = true;
    if (form.observaciones.trim().length < 50) e.observaciones = true; // concepto detallado: mínimo 50 caracteres
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Lo que se manda: el backend recalcula el total y resuelve la cuenta contable de
  // cada motivo, así que aquí sólo se arma el desglose.
  const buildPayload = () => ({
    ...form,
    monto: Math.round(totalConceptos * 100) / 100,
    conceptosComprobacion: conceptos.map(c => ({
      motivo: (c.motivo || '').trim(),
      monto: parseFloat(c.monto) || 0,
      folioCfdi: c.folioCfdi || '', serie: c.serie || '', usoCfdi: c.usoCfdi || '',
      metodoPago: c.metodoPago || '', fechaTimbrado: c.fechaTimbrado || '',
      ...soloFiscales(c),
      impuestos: c._impuestos || [], impuestosImportes: c._impuestosImportes || {},
    })),
  });

  const handleGuardar = async () => {
    if (!validate()) { toast_('Completa los campos requeridos', 'error'); return; }
    setLoading(true);
    try {
      const comp = await api.post('/comprobaciones', buildPayload());
      // La comprobación ya existe: si un archivo falla se avisa sin perderla, y
      // los que faltan se pueden adjuntar desde Captura de Comprobantes.
      try {
        await subirArchivosConceptos(comp._id);
        toast_(`Comprobación ${comp.folio} guardada`);
      } catch (e) {
        toast_(`Comprobación ${comp.folio} guardada, PERO no se adjuntaron los archivos: ${e.message}`, 'error');
      }
      if (onCreated) onCreated();
      setForm(f => ({ ...f, comprobador: '', banco: '', clabe: '', cuenta: '', conceptoGasto: '', monto: '', observaciones: '', folioCfdi: '' }));
      setConceptos([{ motivo: '', monto: '', _archivos: [] }]);
      setConceptosComprobador([]);
    } catch (e) { toast_(e.message, 'error'); }
    finally { setLoading(false); }
  };

  const handlePDF = async () => {
    if (!validate()) { toast_('Completa los campos requeridos', 'error'); return; }
    setLoading(true);
    try {
      const comp = await api.post('/comprobaciones', buildPayload());
      try { await subirArchivosConceptos(comp._id); }
      catch (e) { toast_(`No se adjuntaron los archivos: ${e.message}`, 'error'); }
      const blob = await api.downloadPdf(`/comprobaciones/${comp._id}/pdf`);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: `Comprobacion_${comp.folio}.pdf` }).click();
      URL.revokeObjectURL(url);
      toast_(`Comprobación ${comp.folio} generada`);
      if (onCreated) onCreated();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setLoading(false); }
  };

  const fmt = n => `$${(parseFloat(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />

      <PageHeader
        icon="🧾"
        title="Nueva Solicitud de Comprobación"
        subtitle={`SC-CPP-P-01-F01 · Versión 1 · ${user?.nombre}`}
        actions={
          <>
            <button
              onClick={() => {
                setForm(f => ({ ...f, empresa: '', sucursal: '', centroCostos: '', direccion: '', comprobador: '', banco: '', clabe: '', cuenta: '', conceptoGasto: '', monto: '', observaciones: '', folioCfdi: '' }));
                setConceptosComprobador([]);
              }}
              style={{ ...css.btnGhost, border: '1px solid rgba(255,255,255,0.18)', fontSize: 12 }}>
              Limpiar
            </button>
            <button onClick={handleGuardar} disabled={loading}
              style={{ ...css.btn('rgba(255,255,255,0.9)', false), color: '#231F20', fontSize: 12 }}>
              {loading ? '...' : '💾 Guardar'}
            </button>
            <button onClick={handlePDF} disabled={loading}
              style={{ ...css.btn(accent), boxShadow: `0 4px 14px ${accent}55`, fontSize: 12, minWidth: 130 }}>
              {loading ? 'Generando...' : '⬇️ Guardar y PDF'}
            </button>
          </>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20 }}>
        {/* Formulario */}
        <div style={{ ...css.card, padding: 28 }}>
          <SectionHeader accent={accent}>Datos Generales</SectionHeader>
          <Grid cols={2} gap={14} style={{ marginBottom: 14 }}>
            <div>
              <Label required>Compañía</Label>
              <select value={form.empresa} onChange={e => handleEmpresa(e.target.value)} style={css.select(errors.empresa)}>
                <option value="">Seleccionar empresa...</option>
                {empresas.map(e => <option key={e._id} value={e.nombre}>{e.nombre}</option>)}
              </select>
            </div>
            <div>
              <Label required>Fecha</Label>
              <input type="date" value={form.fechaSolicitud} readOnly style={{ ...css.input(false), ...css.inputRO }} />
            </div>
          </Grid>
          <Grid cols={3} gap={14} style={{ marginBottom: 14 }}>
            <div>
              <Label required>Sucursal</Label>
              <select value={form.sucursal} onChange={e => handleSucursal(e.target.value)} disabled={!form.empresa} style={css.select(errors.sucursal)}>
                <option value="">Seleccionar...</option>
                {sucursalesFiltradas.map(s => <option key={s._id} value={s.nombre}>{s.nombre}</option>)}
              </select>
            </div>
            <div>
              <Label required>Centro de Costos</Label>
              <select value={form.centroCostos} onChange={e => handleCentro(e.target.value)} disabled={!form.sucursal} style={css.select(errors.centroCostos)}>
                <option value="">Seleccionar...</option>
                {(() => {
                  const secs = [...new Set(centros.map(c => (c.seccion || c.direccion || '').trim()).filter(Boolean))];
                  const sinSec = centros.filter(c => !(c.seccion || c.direccion || '').trim());
                  return [
                    ...secs.map(sec => (
                      <optgroup key={sec} label={sec}>
                        {centros.filter(c => (c.seccion || c.direccion || '').trim() === sec).map(c => (
                          <option key={c._id} value={c.nombre}>{c.nombre}</option>
                        ))}
                      </optgroup>
                    )),
                    ...sinSec.map(c => <option key={c._id} value={c.nombre}>{c.nombre}</option>),
                  ];
                })()}
              </select>
            </div>
            <div>
              <Label>Dirección</Label>
              <select value={form.direccion} onChange={e => set('direccion', e.target.value)} style={css.select(false)}>
                <option value="">Seleccionar...</option>
                {['DIRECCION GENERAL','DIRECCION COMERCIAL','DIRECCION COMPRAS','DIRECCION JURIDICO RH',
                  'DIRECCION FINANZAS','DIRECCION UNE CRISTALERIA','DIRECCION UNE MEDICAL STORE',
                  'DIRECCION LOGISTICA','DIRECCION GOLDEN','DIRECCION PATRIMONIAL','DIRECCION FONTASTIC',
                ].map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </Grid>

          <SectionHeader accent={accent}>Comprobador de Gastos</SectionHeader>
          <div style={{ marginBottom: 14 }}>
            <Label required>Buscar Comprobador</Label>
            <ComprobadorSearch
              value={form.comprobador}
              onChange={v => { setForm(f => ({ ...f, comprobador: v, banco: '', clabe: '', cuenta: '', conceptoGasto: '' })); setConceptosComprobador([]); }}
              onSelect={handleComprobador}
            />
            {errors.comprobador && <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 3 }}>Campo requerido</div>}
          </div>
          <Grid cols={3} gap={14} style={{ marginBottom: 14 }}>
            <div><Label>Banco</Label><input value={form.banco} readOnly style={{ ...css.input(false), ...css.inputRO }} /></div>
            <div>
              <Label>CLABE</Label>
              <input value={form.clabe} readOnly style={{ ...css.input(false), ...css.inputRO, fontFamily: 'Geist Mono, monospace', fontSize: 11.5 }} />
              {form.clabe && (() => {
                const r = validarCLABE(form.clabe);
                if (!r) return null;
                return (
                  <div style={{ fontSize: 11, marginTop: 3, fontWeight: 600, color: r.valida ? '#15803d' : '#dc2626' }}>
                    {r.valida ? '✓' : '✗'} {r.mensaje}
                  </div>
                );
              })()}
            </div>
            <div><Label>No. Cuenta</Label><input value={form.cuenta} readOnly style={{ ...css.input(false), ...css.inputRO, fontFamily: 'Geist Mono, monospace', fontSize: 11.5 }} /></div>
          </Grid>
          <div style={{ marginBottom: 14 }}>
            <Label required>Concepto de Pago (Gasto)</Label>
            <ConceptoSearch
              value={form.conceptoGasto}
              onChange={v => set('conceptoGasto', v)}
              hasError={errors.conceptoGasto}
              conceptos={conceptosComprobador}
            />
            {errors.conceptoGasto && <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 3 }}>Campo requerido</div>}
          </div>

          {/* Desglose por motivo de pago: cada gasto con su motivo, su monto y su factura */}
          <div style={{ marginBottom: 14 }}>
            <Label required>Motivos de Pago (desglose de la comprobación)</Label>
            {conceptos.map((_, i) => (
              <React.Fragment key={i}>
                <input ref={el => { cXmlRefs.current[i] = el; }} type="file" accept=".xml,text/xml,application/xml"
                  style={{ display: 'none' }} onChange={e => handleImportXMLConcepto(i, e.target.files)} />
                <input ref={el => { cPdfRefs.current[i] = el; }} type="file" accept=".pdf,application/pdf" multiple
                  style={{ display: 'none' }} onChange={e => handleAdjuntarPDFConcepto(i, e.target.files)} />
              </React.Fragment>
            ))}
            <ConceptosComprobacion
              conceptos={conceptos} onChange={setConceptos} motivos={motivosCatalogo}
              hasError={errors.conceptos} importandoIdx={importandoIdx}
              onImportarXML={i => cXmlRefs.current[i]?.click()}
              onAdjuntarPDF={i => cPdfRefs.current[i]?.click()}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <SectionHeader accent={accent}>Datos de Pago</SectionHeader>
            <div>
              <input ref={xmlInputRef} type="file" accept=".xml,text/xml,application/xml" multiple
                style={{ display: 'none' }} onChange={e => handleImportXML(e.target.files)} />
              <button onClick={() => xmlInputRef.current?.click()} disabled={importando} style={{
                padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                border: '1.5px solid rgba(22,163,74,0.28)', background: 'rgba(22,163,74,0.10)', color: 'var(--ok)',
                cursor: importando ? 'wait' : 'pointer', fontFamily: 'Inter, sans-serif',
              }} title="Lee el XML del SAT y autocompleta monto y folio fiscal">
                {importando ? '⏳ Leyendo...' : '📄 Importar XML'}
              </button>
            </div>
          </div>
          <Grid cols={3} gap={14} style={{ marginBottom: 14 }}>
            <div>
              {/* Ya no se captura: sale de la suma de los motivos, para que el total
                  y el desglose no puedan contradecirse. */}
              <Label>Monto ($)</Label>
              <input value={fmt(totalConceptos)} readOnly title="Suma de los motivos de pago capturados arriba"
                style={{ ...css.input(false), ...css.inputRO, fontFamily: 'Geist Mono, monospace', fontWeight: 700 }} />
              <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 3 }}>Suma de los motivos de pago</div>
            </div>
            <div>
              <Label>Moneda</Label>
              <select value={form.moneda} onChange={e => set('moneda', e.target.value)} style={css.select(false)}>
                <option value="MXN">MXN</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
            <div>
              <Label>Folio(s) CFDI</Label>
              <input value={form.folioCfdi} onChange={e => set('folioCfdi', e.target.value)}
                placeholder="Se llena al importar XML" style={{ ...css.input(false), fontFamily: 'Geist Mono, monospace', fontSize: 11 }} />
            </div>
          </Grid>

          <div>
            <Label required>Concepto del Pago</Label>
            <textarea
              value={form.observaciones}
              onChange={e => set('observaciones', e.target.value)}
              rows={3}
              placeholder="Describe con detalle qué se está pagando (mínimo 50 caracteres)..."
              style={{ ...css.input(errors.observaciones), resize: 'vertical' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 3 }}>
              {errors.observaciones
                ? <span style={{ color: 'var(--danger)', fontSize: 11 }}>Mínimo 50 caracteres — describe el concepto con más detalle</span>
                : <span />}
              <span style={{ fontSize: 11, fontFamily: 'Geist Mono, monospace', color: form.observaciones.trim().length >= 50 ? '#15803d' : 'var(--glass)' }}>
                {form.observaciones.trim().length}/50
              </span>
            </div>
          </div>
        </div>

        {/* Panel resumen */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: 'var(--panel)', borderRadius: 14, overflow: 'hidden', border: '1px solid var(--hair)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
            <div style={{ background: `linear-gradient(135deg, ${accent}22 0%, ${accent}08 100%)`, borderBottom: `1px solid ${accent}30`, padding: '16px 18px', transition: 'background 0.3s' }}>
              <div style={{ fontSize: 10, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Compañía</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', marginTop: 3 }}>{form.empresa || '—'}</div>
            </div>
            <div style={{ padding: '14px 18px' }}>
              {[
                ['Comprobador', form.comprobador],
                ['Concepto', form.conceptoGasto],
                ['Sucursal', form.sucursal],
                ['Centro Costos', form.centroCostos],
              ].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--hair)' }}>
                  <span style={{ fontSize: 11, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{l}</span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink)', maxWidth: 140, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v || '—'}</span>
                </div>
              ))}
              {form.monto && !isNaN(parseFloat(form.monto)) && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--hair)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                    <span style={{ fontSize: 10, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total a Comprobar</span>
                    <div>
                      <span style={{ fontSize: 18, fontWeight: 700, color: accent, transition: 'color 0.3s', fontFamily: 'Geist Mono, monospace' }}>
                        {fmt(form.monto)}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--glass)', marginLeft: 4 }}>{form.moneda}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <button onClick={handlePDF} disabled={loading}
            style={{ ...css.btn(accent), padding: '13px', fontSize: 13.5, width: '100%', boxShadow: `0 4px 14px ${accent}33` }}>
            {loading ? 'Generando...' : '⬇️ Guardar y PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}
